// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import TableLoading from 'components/atoms/TableLoading';
import { IconAddCircleOutline } from 'components/atoms/icons/IconAddCircleOutline';
import { IconDelete } from 'components/atoms/icons/IconDelete';
import { IconEdit } from 'components/atoms/icons/IconEdit';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import { CreateUpdateTemporaryStorageFormInputType } from 'components/molecules/CreateUpdateTemporaryStorageInputForm';
import DeleteItemModal from 'components/molecules/DeleteItemForm/DeleteItemModal';
import DOMPurify from 'dompurify';
import _debounce from 'lodash/debounce';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  DEFAULT_PAGE,
  PAGE_SIZE,
  TEMPORARY_STORAGE_COLORS_PALETTE,
} from 'util/ConstantValues';
import CreateUpdateTemporaryStorageModal from '../CreateUpdateTemporaryStorageModal';
import styles from './index.module.scss';
import { createAxios } from 'ts/createAxios';
import { toast } from 'react-toastify';
import { handleError } from 'util/errorHandler';
import { API_ENDPOINTS } from 'util/endPoints';
import { getApiPath } from 'util/commons';

export type FetchTemporaryStorageData = {
  temporaryStorages: TemporaryStorage[];
  total: number;
  page: number;
  operatorName: string;
  siteName: string;
};
export type TemporaryStorage = {
  id: number;
  name: string;
  color: string;
};

interface ModalState {
  isOpen: boolean;
  type: CreateUpdateTemporaryStorageFormInputType;
  itemId?: string;
  dataTemporaryStorageEdit?: TemporaryStorage;
}

interface Props {
  onSetBreadcrumb: (operatorName: string, siteName: string) => void;
}

const TemporaryStorageManagement: React.FC<Props> = ({ onSetBreadcrumb }) => {
  const { t } = useTranslation();
  const api = createAxios();
  const [temporaryStorages, setTemporaryStorages] = useState<
    TemporaryStorage[]
  >([]);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [total, setTotal] = useState(0);
  const [deleteItems, setDeleteItems] = useState<TemporaryStorage>();
  const [search, setSearch] = useState('');
  const [isOpenCreate, setOpenCreate] = useState<ModalState>({
    isOpen: false,
    type: CreateUpdateTemporaryStorageFormInputType.add,
  });

  const { siteId } = useParams<{
    siteId: string;
  }>();

  const fetchTemporaryStorages = async (
    keyword?: string,
    nextPage?: number,
    forceRefresh?: boolean
  ) => {
    const params = new URLSearchParams([['siteId', siteId]]);
    if (keyword) {
      params.append('keyword', keyword);
    }

    if (nextPage || !keyword) {
      params.append('page', String(nextPage || DEFAULT_PAGE));
      params.append('take', String(PAGE_SIZE));
    }

    const fetchedData = await api.get<FetchTemporaryStorageData>(
      API_ENDPOINTS.TEMPORARY_STORAGES,
      { params }
    );

    if (keyword) {
      setTemporaryStorages(fetchedData.data.temporaryStorages);
    } else {
      setTemporaryStorages(
        forceRefresh
          ? fetchedData.data.temporaryStorages
          : [...temporaryStorages, ...fetchedData.data.temporaryStorages]
      );
    }
    setTotal(fetchedData.data.total);
    setCurrentPage(fetchedData.data.page);
    const { siteName, operatorName } = fetchedData.data;
    onSetBreadcrumb(operatorName, siteName);
  };

  useEffect(() => {
    fetchTemporaryStorages();
  }, []);

  const handleClickAdd = () => {
    setOpenCreate({
      isOpen: true,
      type: CreateUpdateTemporaryStorageFormInputType.add,
    });
  };

  const handleClickEdit = (e: ChangeEvent, item: TemporaryStorage) => {
    setOpenCreate({
      isOpen: true,
      type: CreateUpdateTemporaryStorageFormInputType.update,
      itemId: String(item.id),
      dataTemporaryStorageEdit: item,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClickDelete = (e: ChangeEvent, item: any) => {
    setDeleteItems(item);
  };

  const debounceFn = useCallback(_debounce(fetchTemporaryStorages, 1000), []);

  const handleChange = (e) => {
    setSearch(e.target.value);
    debounceFn(e.target.value.trim());
  };

  const handleTemporaryStorageDelete = async (id) => {
    api
      .delete(getApiPath(API_ENDPOINTS.TEMPORARY_STORAGE_DETAIL, { id }))
      .then(function () {
        fetchTemporaryStorages('', 1, true);
        toast.success(t('messages.M_012'));
      })
      .catch(function (e) {
        handleError(e);
      });
    setDeleteItems(undefined);
  };

  const handleSearch = useCallback(() => {
    search && fetchTemporaryStorages(search);
  }, [search]);

  const newColorIndex = useMemo(() => {
    if (!temporaryStorages.length) {
      return 0;
    }

    const indexedColors = temporaryStorages.map(({ color }) => Number(color));

    for (let i = 0; i < TEMPORARY_STORAGE_COLORS_PALETTE.length; i++) {
      if (!indexedColors.includes(i)) {
        return i;
      }
    }

    return total % TEMPORARY_STORAGE_COLORS_PALETTE.length;
  }, [temporaryStorages, total]);

  return (
    <div className={styles.temporaryStorageManagement}>
      <div
        className="count-list"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            t('common.page', {
              page: total,
            }) ?? ''
          ),
        }}
      />

      <div className={`search-area ${styles.searchArea}`}>
        <div className="search-input">
          <input
            placeholder={t('common.placeholder.search') || '検索'}
            value={search}
            onChange={handleChange}
          />

          <div className="icon" onClick={handleSearch}>
            <IconSearch />
          </div>
        </div>

        <div className="button" onClick={handleClickAdd}>
          <IconAddCircleOutline />
          <span>{t('temporary_storage_management.button_add')}</span>
        </div>
      </div>
      <div className={`scroll-wrapper ${styles.scrollWrapper}`}>
        <table id={styles.customizeItemTable}>
          <thead>
            <tr>
              <th className={styles.name}>
                {t('temporary_storage_management.thead_name')}
              </th>
              <th className={styles.action}>{t('common.action')}</th>
            </tr>
          </thead>
          <tbody>
            {temporaryStorages.length ? (
              temporaryStorages.map((item, i) => {
                return (
                  <tr key={i}>
                    <td className={styles.name}>
                      <div className={styles.contentName} title={item.name}>
                        <p>{item.name}</p>
                      </div>
                    </td>
                    <td className={styles.action}>
                      <div className={styles.contentAction}>
                        <div className={styles.actionEdit}>
                          <IconEdit
                            onClick={(e: ChangeEvent) => {
                              e.stopPropagation();
                              handleClickEdit(e, item);
                            }}
                          />
                        </div>
                        <div className={styles.actionDelete}>
                          <IconDelete
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickDelete(e, item);
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="msg-error">{t('messages.M_008')}</td>
              </tr>
            )}
            {temporaryStorages && temporaryStorages.length > 0 && (
              <TableLoading
                onNext={() => {
                  if (
                    temporaryStorages.length >= PAGE_SIZE &&
                    temporaryStorages.length < total
                  ) {
                    setCurrentPage(currentPage + 1);
                    fetchTemporaryStorages(search, currentPage + 1);
                  }
                }}
              />
            )}
          </tbody>
        </table>

        {isOpenCreate.isOpen && (
          <CreateUpdateTemporaryStorageModal
            dataSuggestions={[]}
            detailTemporaryStorage={isOpenCreate.dataTemporaryStorageEdit}
            onSubmit={() => {
              fetchTemporaryStorages('', DEFAULT_PAGE, true);
            }}
            onClose={() => {
              setOpenCreate({
                isOpen: false,
                type: CreateUpdateTemporaryStorageFormInputType.add,
                itemId: undefined,
              });
            }}
            type={isOpenCreate.type}
            itemId={isOpenCreate.itemId}
            colorIndex={newColorIndex}
          />
        )}

        {deleteItems && (
          <DeleteItemModal
            item={deleteItems}
            onClose={() => setDeleteItems(undefined)}
            onHandleDelete={handleTemporaryStorageDelete}
          />
        )}
      </div>
    </div>
  );
};

export default TemporaryStorageManagement;
